import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { attendanceService } from "../../api/attendanceService";
import { correctionService } from "../../api/correctionService";
import Modal from "../../components/ui/Modal";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { formatManilaTime } from "../../utils/manilaTime";
import { parseDateKey } from "../../utils/date";
import { cx } from "../../utils/classNames";
import {
  ADD_SESSION,
  CORRECTION_KEY,
  MAX_REASON,
  buildCorrection,
  timeOf,
} from "./correctionUtils";
import ui from "../../components/ui/ui.module.css";

const sessionLabel = (s, i) =>
  `Record ${i + 1}: ${formatManilaTime(s.checkInTime)} – ${s.checkOutTime ? formatManilaTime(s.checkOutTime) : "no check-out"}`;

// Ask the DTR approver to fix a day: change a check-in / check-out, or add a
// missing record. Applied only once approved; the week can't be submitted meanwhile.
const RequestCorrectionModal = ({ date, attendanceId, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const sessionsQuery = useQuery({
    queryKey: [CORRECTION_KEY, "sessions", attendanceId],
    queryFn: () => attendanceService.getLocations(attendanceId),
    enabled: !!attendanceId,
  });
  const sessions = (sessionsQuery.data?.sessions ?? []).filter((s) => s.id);
  const [form, setForm] = useState({
    sessionId: ADD_SESSION,
    checkIn: "",
    checkOut: "",
    reason: "",
  });
  const [problem, setProblem] = useState(null);
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const chosen = sessions.find((s) => s.id === form.sessionId);

  // Start on the record without a check-out (the usual problem), else the first
  useEffect(() => {
    if (!sessions.length) return;
    const s = sessions.find((x) => !x.checkOutTime) ?? sessions[0];
    setForm((f) => ({
      ...f,
      sessionId: s.id,
      checkIn: timeOf(s.checkInTime),
      checkOut: timeOf(s.checkOutTime),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsQuery.data]);

  const pick = (id) => {
    const s = sessions.find((x) => x.id === id);
    set({
      sessionId: id,
      checkIn: s ? timeOf(s.checkInTime) : "",
      checkOut: s ? timeOf(s.checkOutTime) : "",
    });
  };

  const mutation = useMutation({
    mutationFn: (body) => correctionService.create(body),
    onSuccess: () => {
      toast.success("Correction sent to your DTR approver.");
      queryClient.invalidateQueries({ queryKey: [CORRECTION_KEY] });
      queryClient.invalidateQueries({ queryKey: ["me:dtr"] });
      onClose();
    },
  });

  const submit = (e) => {
    e.preventDefault();
    const { body, error } = buildCorrection(date, form, chosen);
    setProblem(error ?? null);
    if (body) mutation.mutate(body);
  };

  const day = parseDateKey(date)?.toLocaleDateString("en-PH", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <Modal
      as="form"
      title="Request a correction"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <button
            type="button"
            className={cx(ui.button, ui.secondary)}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={cx(ui.button, ui.primary)}
            disabled={mutation.isPending || sessionsQuery.isLoading}
          >
            {mutation.isPending ? "Sending…" : "Send for approval"}
          </button>
        </>
      }
    >
      <div className={ui.formStack}>
        <div className={cx(ui.alert, ui.alertInfo)}>
          <span>
            {day}. Your DTR approver checks it; the day changes only once it is
            approved. You can submit this week after that.
          </span>
        </div>

        {sessionsQuery.isLoading ? (
          <span className={ui.hint}>Loading the day…</span>
        ) : (
          <div>
            <label htmlFor="corr-session" className={ui.label}>
              What needs fixing
            </label>
            <select
              id="corr-session"
              className={ui.input}
              value={form.sessionId}
              onChange={(e) => pick(e.target.value)}
            >
              {sessions.map((s, i) => (
                <option key={s.id} value={s.id}>
                  {sessionLabel(s, i)}
                </option>
              ))}
              <option value={ADD_SESSION}>
                Add a missing check-in / check-out
              </option>
            </select>
          </div>
        )}

        <div className={ui.formRow}>
          <div>
            <label htmlFor="corr-in" className={ui.label}>
              Check-in
            </label>
            <input
              id="corr-in"
              type="time"
              className={ui.input}
              value={form.checkIn}
              onChange={(e) => set({ checkIn: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="corr-out" className={ui.label}>
              Check-out
            </label>
            <input
              id="corr-out"
              type="time"
              className={ui.input}
              value={form.checkOut}
              onChange={(e) => set({ checkOut: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label htmlFor="corr-reason" className={ui.label}>
            Reason
          </label>
          <textarea
            id="corr-reason"
            rows={3}
            maxLength={MAX_REASON}
            className={cx(ui.input, ui.textarea)}
            value={form.reason}
            onChange={(e) => set({ reason: e.target.value })}
            placeholder="e.g. Forgot to check out; left at 6:00 PM"
            required
          />
        </div>

        {(problem || mutation.error) && (
          <div className={cx(ui.alert, ui.alertDanger)}>
            {problem ||
              getErrorMessage(mutation.error, "Unable to send the correction.")}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RequestCorrectionModal;
